import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SnackbarStatus, StatusBar, Typography } from '@weareopenr/component-library';
import { IRuleCalculationFields } from 'src/types/priceRules';
import { INestedPricingCategory, IPricingCategory } from 'src/api/getPricingCategories/types';
import { AdjustmentType, RoundingType } from '@weareopenr/types';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import {
  createPriceRuleAdjustment,
  createPriceRuleRounding,
  defaultAdjustmentValues,
  defaultRoundingValues,
} from 'src/utils/priceRules/helpers';
import {
  buildCalculationSubtitle,
  buildRoundingSubtitle,
  getTitles,
  TPriceRuleTitleStep,
} from 'src/utils/priceRules/confirmationStrings';
import { createPriceRules } from 'src/api/createPriceRules';
import { updatePriceRule } from 'src/api/updatePriceRule/updatePriceRule';
import PriceRuleAdjustmentOptions from '../PriceRuleAdjustmentOptions';
import { RoundingRuleOptions } from '../RoundingRuleOptions';
import PriceRuleContinueButtons from '../PriceRuleFlowShared/PriceRuleContinueButtons';
import { PricingCategorySelectorStep } from './Steps/PricingCategorySelectorStep';
import { PricingCategoryOverviewStep } from './Steps/PricingCategoryOverviewStep';

import styles from './CategoryOverrideRuleModal.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import { PRICE_RULES_QUERY, QueryKeys } from 'src/hooks/QueryKeys';
import { UNCATEGORISED_ID } from 'src/utils/pricingCategories';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { IPriceRule } from 'src/types/price.types';

export enum CategoryOverrideRuleSteps {
  CategorySelector = 0,
  Adjustment = 1,
  Rounding = 2,
  Confirm = 3,
}

const TOTAL_STEPS = 4;

const createSteps: TPriceRuleTitleStep[] = [
  {
    title: 'Select Categories',
    subtitle: 'Select the pricing categories you would like to create an override rule for.',
  },
  {
    title: 'Override Calculation',
    subtitle: 'Automate pricing adjustments for these categories based on default product values.',
  },
  {
    title: 'Override Rounding',
    subtitle: 'Create a rounding rule for all automated price changes within these categories.',
  },
];

const isContinueDisabled = (
  step: CategoryOverrideRuleSteps,
  { entities, adjustment, rounding }: IRuleCalculationFields,
): boolean => {
  switch (step) {
    case CategoryOverrideRuleSteps.CategorySelector:
      return entities.length === 0;
    case CategoryOverrideRuleSteps.Adjustment: {
      const adjustmentEnabled = adjustment.type !== AdjustmentType.NONE;
      const noValueExists = Number.isNaN(adjustment.fixed) && Number.isNaN(adjustment.percentage);
      return adjustmentEnabled && noValueExists;
    }
    case CategoryOverrideRuleSteps.Rounding: {
      const roundingEnabled = rounding.type !== RoundingType.NONE && rounding?.method !== undefined;
      const noValueExists = rounding?.value === undefined || Number.isNaN(rounding.value);
      return roundingEnabled && noValueExists;
    }
    default:
      return false;
  }
};

type CategoryOverrideRuleModalContentProps = {
  closeModal: () => void;
  priceSchemeId: string;
  categories: INestedPricingCategory[];
  flatCategories: IPricingCategory[];
  rules?: IPriceRule[];
  // Create mode only
  parentId?: string;
  defaultCategoryId?: string;
  parentName?: string;
  parentType?: 'price_band' | 'extension_instance';
  // Edit mode
  ruleToEdit?: IPriceRule;
  defaultStep?: CategoryOverrideRuleSteps;
};

export const CategoryOverrideRuleModalContent = ({
  closeModal,
  priceSchemeId,
  defaultCategoryId,
  parentId,
  parentName,
  parentType,
  categories,
  flatCategories,
  rules,
  ruleToEdit,
  defaultStep,
}: CategoryOverrideRuleModalContentProps) => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();

  const isEditMode = !!ruleToEdit;

  const [step, setStep] = useState<CategoryOverrideRuleSteps>(
    defaultStep ?? (isEditMode ? CategoryOverrideRuleSteps.Adjustment : CategoryOverrideRuleSteps.CategorySelector),
  );

  const flatten = (c: INestedPricingCategory): INestedPricingCategory[] => [c, ...c.children.flatMap(flatten)];

  const getDefaultEntities = (): string[] => {
    if (!defaultCategoryId) return [];

    const findCategory = (cats: INestedPricingCategory[]): INestedPricingCategory | undefined => {
      for (const cat of cats) {
        if ((cat.category_id ?? UNCATEGORISED_ID) === defaultCategoryId) return cat;
        const found = findCategory(cat.children);
        if (found) return found;
      }
      return undefined;
    };

    const defaultCategory = findCategory(categories);
    if (!defaultCategory) return [];

    const disabledIds = new Set(
      rules?.map((r) => r.pricing_category?.id ?? r.pricing_category_id).filter(Boolean) ?? [],
    );
    return flatten(defaultCategory)
      .map((c) => c.category_id ?? UNCATEGORISED_ID)
      .filter((id) => !disabledIds.has(id));
  };

  const { handleSubmit, register, formState, setValue, watch, control } = useForm<IRuleCalculationFields>({
    defaultValues: {
      adjustment: defaultAdjustmentValues(ruleToEdit?.adjustment),
      rounding: defaultRoundingValues(ruleToEdit?.rounding),
      entities: isEditMode ? [] : getDefaultEntities(),
    },
  });

  const { isSubmitting } = formState;
  const { entities, adjustment, rounding } = watch();

  const showConfirmBtn = step === CategoryOverrideRuleSteps.Confirm || isEditMode;
  const showCancelBtn = step === CategoryOverrideRuleSteps.CategorySelector || isEditMode;
  const continueDisabled = isContinueDisabled(step, { entities, adjustment, rounding });

  const decrementStep = () => setStep((s) => s - 1);
  const incrementStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setStep((s) => s + 1);
  };

  const onSubmit = async ({ entities: categoryIds, adjustment, rounding }: IRuleCalculationFields) => {
    clearError();
    try {
      if (isEditMode) {
        await updatePriceRule(
          [
            {
              price_rule_id: ruleToEdit.price_rule_id,
              adjustment: createPriceRuleAdjustment(adjustment),
              rounding: createPriceRuleRounding(rounding),
            },
          ],
          priceSchemeId,
        );
        showNotification({ status: SnackbarStatus.SUCCESS, message: 'Category override rule updated.' });
      } else {
        await createPriceRules(
          categoryIds.map((categoryId) => ({
            price_band_id: parentType === 'price_band' ? parentId : undefined,
            extension_instance_id: parentType === 'extension_instance' ? parentId : undefined,
            pricing_category_id: categoryId,
            adjustment: createPriceRuleAdjustment(adjustment),
            rounding: createPriceRuleRounding(rounding),
          })),
          priceSchemeId,
        );
        showNotification({ status: SnackbarStatus.SUCCESS, message: 'Category override rule created.' });
      }
      queryClient.invalidateQueries({ exact: false, queryKey: [...PRICE_RULES_QUERY, priceSchemeId] });
      queryClient.invalidateQueries({ exact: false, queryKey: [getBrandId(), QueryKeys.priceBands, priceSchemeId] });
      closeModal();
    } catch (error) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message:
          error instanceof Error
            ? error.message
            : isEditMode
              ? 'Failed to update override rule.'
              : 'Failed to create override rule.',
      });
    }
  };

  const roundingSubtitle = useMemo(() => buildRoundingSubtitle(rounding), [step, rounding]);
  const calculationSubtitle = useMemo(() => buildCalculationSubtitle(adjustment), [step, adjustment]);

  const [staticTitle, subtitle] = getTitles(step, createSteps);

  const selectedCategories = flatCategories.filter((c) => entities.includes(c.category_id ?? UNCATEGORISED_ID));

  const title =
    step === CategoryOverrideRuleSteps.CategorySelector ? (
      <span>
        Create Category Rules for <b>{parentName}</b>
      </span>
    ) : (
      staticTitle
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)} aria-label="category-override-rule-form" className={styles['form']}>
      {!isEditMode && <Typography className={styles['steps']}>{`Step ${step + 1} of ${TOTAL_STEPS}`}</Typography>}
      <Typography className={styles['title']} size="xxlarge">
        {title}
      </Typography>
      {step !== CategoryOverrideRuleSteps.CategorySelector && (
        <Typography className={styles['subtitle']}>{subtitle}</Typography>
      )}

      {step === CategoryOverrideRuleSteps.CategorySelector && (
        <PricingCategorySelectorStep
          categories={categories}
          selectedCategoryIds={entities}
          onChange={(ids) => setValue('entities', ids)}
          rules={rules}
        />
      )}

      {step === CategoryOverrideRuleSteps.Adjustment && (
        <PriceRuleAdjustmentOptions register={register as any} control={control as any} entity="Override" />
      )}

      {step === CategoryOverrideRuleSteps.Rounding && (
        <RoundingRuleOptions register={register as any} control={control as any} errors={formState.errors} />
      )}

      {step === CategoryOverrideRuleSteps.Confirm && (
        <PricingCategoryOverviewStep
          parentName={parentName}
          parentType={parentType}
          selectedCategories={selectedCategories}
          flatCategories={flatCategories}
          calculationSubtitle={calculationSubtitle}
          roundingSubtitle={roundingSubtitle}
        />
      )}

      <PriceRuleContinueButtons
        showCancelBtn={showCancelBtn}
        closeModal={closeModal}
        decrementStep={decrementStep}
        showConfirmBtn={showConfirmBtn}
        isSubmitting={isSubmitting}
        continueDisabled={continueDisabled}
        incrementStep={incrementStep}
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};
